import type { MyFacilityListRow } from "types/myFacilityListRow";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

const API_BASE_URL = getApiOrigin();
const MyfacillitiesEndpoint = "/api/v1/facility-management/my-facilities";

/** Session-friendly TTL: duplicate mounts / navigations share one GET per country. */
const MY_FACILITIES_CACHE_TTL_MS = 120_000;
/** Empty lists are not cached long — new invites may populate seconds after first login. */
const EMPTY_MY_FACILITIES_CACHE_TTL_MS = 5_000;

function myFacilitiesCacheTtlMs(facilities: MyFacilityListRow[]): number {
  return facilities.length > 0
    ? MY_FACILITIES_CACHE_TTL_MS
    : EMPTY_MY_FACILITIES_CACHE_TTL_MS;
}

let _myFacilitiesCache: {
  at: number;
  countryCode: string;
  vertical: string;
  facilities: MyFacilityListRow[];
} | null = null;

/** Single-flight: concurrent callers await one in-flight GET per country + vertical. */
const _myFacilitiesInFlight = new Map<string, Promise<MyFacilityListRow[]>>();

/** Staggered bypassCache calls within this window reuse the last successful result. */
const MY_FACILITIES_BYPASS_COALESCE_MS = 3_000;
let _lastBypassResult: {
  key: string;
  at: number;
  facilities: MyFacilityListRow[];
} | null = null;

export function clearMyFacilitiesBypassCoalesceForTests(): void {
  _lastBypassResult = null;
}

function _myFacilitiesCacheKey(countryCode: string, vertical: string): string {
  return `${countryCode}\0${vertical}`;
}

/** Clears the in-memory facility list cache (e.g. after mutations). */
export function invalidateMyFacilitiesCache(): void {
  _myFacilitiesCache = null;
  _lastBypassResult = null;
}

export const facilityService = {
  /**
   * Fetch facilities for the selected country. Uses a short-lived in-memory cache unless
   * `bypassCache` is true (e.g. after profile updates or explicit refresh).
   */
  async getMyFacilities(options?: {
    bypassCache?: boolean;
    /** Vertical slug filter; defaults to healthcare when omitted. */
    vertical?: string;
  }): Promise<MyFacilityListRow[]> {
    try {
      const countryCode = getSelectedCountryCode();
      const vertical = (options?.vertical || "healthcare").trim().toLowerCase() || "healthcare";
      const cacheKey = _myFacilitiesCacheKey(countryCode, vertical);
      const now = Date.now();
      if (
        !options?.bypassCache &&
        _myFacilitiesCache &&
        _myFacilitiesCache.countryCode === countryCode &&
        _myFacilitiesCache.vertical === vertical &&
        now - _myFacilitiesCache.at <
          myFacilitiesCacheTtlMs(_myFacilitiesCache.facilities)
      ) {
        return _myFacilitiesCache.facilities;
      }

      const inflight = _myFacilitiesInFlight.get(cacheKey);
      if (inflight) {
        return inflight;
      }

      if (options?.bypassCache) {
        if (
          _lastBypassResult &&
          _lastBypassResult.key === cacheKey &&
          now - _lastBypassResult.at <
            myFacilitiesCacheTtlMs(_lastBypassResult.facilities)
        ) {
          return _lastBypassResult.facilities;
        }
        _myFacilitiesCache = null;
      }

      const run = async (): Promise<MyFacilityListRow[]> => {
        const url = `${API_BASE_URL}${MyfacillitiesEndpoint}?country_code=${encodeURIComponent(countryCode)}&vertical=${encodeURIComponent(vertical)}`;
        const response = await authenticatedFetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          const httpError = new Error(
            `HTTP error! status: ${response.status}`,
          ) as Error & { httpStatus?: number; licenseCode?: string };
          httpError.httpStatus = response.status;
          if (response.status === 402) {
            try {
              const body = (await response.json()) as { code?: string };
              httpError.licenseCode = body?.code;
            } catch {
              /* non-JSON 402 */
            }
          }
          throw httpError;
        }

        const data = (await response.json()) as { facilities?: MyFacilityListRow[] };
        const facilities = data.facilities ?? [];
        _myFacilitiesCache = {
          at: Date.now(),
          countryCode,
          vertical,
          facilities,
        };
        if (options?.bypassCache) {
          _lastBypassResult = { key: cacheKey, at: Date.now(), facilities };
        }
        return facilities;
      };

      const promise = run().finally(() => {
        _myFacilitiesInFlight.delete(cacheKey);
      });
      _myFacilitiesInFlight.set(cacheKey, promise);
      return promise;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error fetching facilities:", message);
      throw error;
    }
  },
};
